import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import * as _ from 'lodash';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import { BusinessEntityService } from 'src/app/services/businessEntity.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import { FarmSiteService } from 'src/app/services/farmSite.service';
import FarmSiteModel from 'src/app/services/farmSite.model';
import { PondSectionService } from 'src/app/services/pondSection.service';
import PondSectionModel from 'src/app/services/pondSection.model';
import CustomerPondModel from 'src/app/services/customerPond.model';
import { CustomerPondService } from 'src/app/services/customerPond.service';
import PondCultureModel from 'src/app/services/pondCulture.model';
import { PondCultureService } from 'src/app/services/pondCulture.service';
import {ActivatedRoute} from '@angular/router';
import { ExcelService } from 'src/app/services/excelService';
import PersonsModel from '../../../services/persons.model';
import {PersonsService} from '../../../services/persons.service';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';

/**
 * @component PondTracebilityComponent
 * @selector app-pond-tracebility
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `PondTracebilityComponent` is responsible for generating and displaying the Pond Traceability Report.
 * This report provides a historical and current view of pond activities, culture details, and traceability metrics
 * essential for farm management and compliance.
 *
 * ## Key Responsibilities:
 * 1. **Hierarchical Data Selection**: Manages a dependent dropdown chain for selecting Business -> Farm Site -> Section -> Pond.
 * 2. **Report Generation**: Fetches traceability data based on the selected pond and optional culture filters.
 * 3. **Data Filtering**: Provides client-side filtering capabilities:
 *    - Text-based search across 'PondType' and 'CultureType'.
 *    - Specific filtering by 'Culture ID' to narrow down history.
 * 4. **Role-Based Access Control**: Adapts the initial data loading and available options based on the logged-in user's role
 *    (e.g., Farm Owner, Farm Manager, Provider, Employee).
 * 5. **Export Functionality**: Enables exporting the generated report to an Excel file.
 * 6. **Performance Optimization**: Uses `BatchLoader` to handle large datasets via infinite scrolling.
 *
 * ## User Roles & Permissions:
 * - **Farm Category**:
 *   - **Business Admin / Farmer**: Access to all farm sites within their business.
 *   - **Farm Manager**: Restricted access to farm sites assigned to them (via Farm Spend context).
 * - **Provider Category**:
 *   - **Business Admin**: Access to farm sites associated with the provider business.
 *   - **Provider Manager**: Access to farm sites managed by technicians under their supervision.
 * - **Employee / Other**: Requires manual selection of a business entity to view reports.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role (e.g., 'Business Admin', 'Farmer').
 * - `loginBusinessCategory`: Business context ('Farm', 'Provider').
 * - `businessID`: ID of the logged-in user's business.
 * - `personID`: ID of the logged-in user.
 * - `loginBusinessName`: Used for report headers.
 *
 * ## Data Flow:
 * 1. **Initialization**: `ngOnInit` determines user context and loads initial Farm Sites or Business Entities.
 * 2. **Selection**: User selects a Farm Site -> Section -> Pond.
 * 3. **Data Fetching**: `traceabilityReport()` calls `PondCultureService` to fetch the report.
 * 4. **Processing**: Data is processed (e.g., formatting weather strings) and loaded into `pondTraceabilitiesData`.
 * 5. **Display**: Data is rendered in a table with infinite scroll support.
 *
 * ## UI State Management:
 * - **Loading States**: Managed via `responseLoader` (API) and `listLoader` (scroll).
 * - **Form Validation**: Managed via `registerForm`.
 * - **Filtering**: Managed via `isFiltering`, `search`, and `isCultureSelected`.
 */
@Component({
  selector: 'app-pond-tracebility',
  templateUrl: './pond-tracebility.component.html',
  styleUrls: ['./pond-tracebility.component.scss']
})
export class PondTracebilityComponent implements OnInit, OnDestroy{
  /** @type {Array<BusinessEntity>} List of available business entities for selection. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {BusinessEntity} The currently selected business entity. */
  public businessEntity: BusinessEntity;

  /** @type {Array<FarmSiteModel>} List of farm sites available for selection. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<PondSectionModel>} List of sections within the selected farm site. */
  public pondSections: Array<PondSectionModel>;

  /** @type {Array<CustomerPondModel>} List of ponds within the selected section. */
  public customerPonds: Array<CustomerPondModel>;

  /** @type {Array<PondCultureModel>} List of cultures associated with a selected pond. */
  public pondCultures: Array<PondCultureModel>;

  /** @type {any} The list of traceability records currently displayed in the UI. */
  public pondTraceabilitiesData: any;

  /** @type {any} The master list of traceability records used for filtering. */
  public dupPondTraceabilities: any;

  /** @type {Array<PondCultureModel>} Alias for `pondCultures` used in dropdown binding. */
  public cultures: Array<PondCultureModel>;

  /** @type {PondCultureModel} The model containing selection criteria (business_id, farm_site_id, section_id, pond_id). */
  public pondCulture: PondCultureModel;

  /** @type {PondCultureModel} Model used specifically for Excel export data structure. */
  public pondCultureTraceabilityExcel: PondCultureModel;

  /** @type {string} The role of the logged-in user. */
  public loginPersonType: string;

  /** @type {string} The name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} The category of the logged-in user's business. */
  public loginUserBusinessCategory: string;

  /** @type {boolean} Flag controlling the main loading spinner for API requests. */
  public responseLoader: boolean;

  /** @type {boolean} Flag indicating if the search/submit button has been clicked. */
  public isSearchClicked: boolean;

  /** @type {Array<any>} Dropdown list for pond details, formatted for display. */
  public pondsDetails_dropdown = new Array();

  /** @type {any} Input data for Excel export (unused in current logic). */
  public excelInputData: any;

  /** @type {string} Host name (unused in current logic). */
  public hostName: string;

  /** @type {number} Current page number for pagination. Default is `1`. */
  public currentPageNumber: number = 1;

  /** @type {number} Number of items per page. Default is `15`. */
  public noOfItemsPerPage: number = 15;

  /** @type {any} Application permissions object. */
  public appPermission: any;

  /** @type {string} Keyword for search operations. */
  public keyword:string;

  /** @type {any} Generic item holder. */
  public item:any;

  /** @type {string} The current search string entered by the user. */
  public search: string;

  /** @type {boolean} Flag to display a "No Data" message. Default is `false`. */
  public NoDataMessage:boolean = false;

  /** @type {number} The current active page number. */
  public activePageNo: number;

  /** @type {boolean} Flag to trigger clearing of the search input. */
  public clearSearchInput: boolean;

  /** @type {Array<PersonsModel>} List of technicians (used for Provider Manager role). */
  public persons: Array<PersonsModel>;

  /** @type {FormGroup} The main reactive form for report criteria selection. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag controlling the visibility of the business selection dropdown. Default is `false`. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered list of options for the business autocomplete dropdown. Default is `[]`. */
  public filteredOptions: any[] = [];

  /** @type {string} Placeholder or value for the business search input. Default is 'Enter the business name'. */
  public businessSearch: string = 'Enter the business name';

  /** @type {boolean} Flag indicating if the form has been submitted. */
  public submitted: boolean;

  /** @type {boolean} Flag to disable the submit button during processing. */
  public submitButtonDisabled: boolean;

  /** @type {string} The type of filter currently active. */
  public filterType: string;

  /** @type {boolean} Flag indicating if filters have been applied. */
  public filtersApplied: boolean;

  /** @type {boolean} Flag indicating if the submit action has been triggered successfully. */
  public isSubmitClicked: boolean;

  /** @type {boolean} Flag indicating if a specific culture is selected for filtering. */
  public isCultureSelected: boolean;

  /** @type {boolean} Flag controlling the spinner for list batch loading. Default is `false`. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} The starting index for batch loading. Default is `0`. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag indicating if client-side filtering is currently active. Default is `false`. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag indicating if the list is empty. Default is `false`. */
  public noList: boolean = false;

  /** @type {BatchLoader<any>} Utility for handling batch loading of data. */
  private batchLoader: BatchLoader<any>;


  /**
   * Constructor for PondTracebilityComponent.
   * Initializes the component and injects necessary services.
   *
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {ExcelService} excelService - Service for exporting data to Excel.
   * @param {PersonsService} personService - Service for managing person/technician data.
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @param {PondCultureService} pondCultureService - Service for pond culture and traceability data.
   * @param {CustomerPondService} customerPondService - Service for customer pond operations.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSitesService - Service for farm site operations.
   * @param {PondSectionService} pondSectionService - Service for pond section operations.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   *
   * @service FormBuilder
   * @service ExcelService
   * @service PersonsService
   * @service ActivatedRoute
   * @service PondCultureService
   * @service CustomerPondService
   * @service BusinessEntityService
   * @service FarmSiteService
   * @service PondSectionService
   * @service Sidenavaction
   * @service LocationStrategy
   * @service PreviousRouteService
   */
  constructor(public formBuilder: FormBuilder, public excelService: ExcelService, public personService: PersonsService, public route: ActivatedRoute, public pondCultureService: PondCultureService, public customerPondService: CustomerPondService, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public pondSectionService: PondSectionService, public sidenavaction: Sidenavaction, private toastr: ToastrService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.pondCulture = new PondCultureModel();
    // Initialize the register form with validation
    this.registerForm = this.formBuilder.group({
      locationField: ['', Validators.required],
      sectionField: ['', Validators.required],
      pondField: ['', Validators.required]
    });
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Sets up the previous route.
   * - Determines the logic flow based on `loginBusinessCategory`:
   *   - **Farm**: Auto-sets business ID and fetches farm sites based on `loginPersonType`.
   *   - **Provider**: Fetches farm sites or technicians based on `loginPersonType`.
   *   - **Default**: Fetches all business entities for selection.
   * - Adjusts form validation based on user role.
   *
   */
  public async ngOnInit() {
    try {
      this.loginPersonType = localStorage.getItem('loginPersonType'); // loginperson type details
      this.search = '';
      this.filterType = '';
      this.filtersApplied = false;
      this.isSubmitClicked = false;
      window.scrollTo(0, 0);
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      switch (localStorage.getItem('loginBusinessCategory')) {
        case 'Farm':
          this.pondCulture.business_id = localStorage.businessID;
          switch (this.loginPersonType) {
            case 'Business Admin':
            case 'Farmer':
              this.farmSites = await this.farmSitesService.getFarmeSitesByFarmsId(localStorage.businessID);
              break;
            default:
              break;
          }
          if (this.farmSites.length === 1) {
            this.pondCulture.farm_site_id = this.farmSites[0].id; // model-binding key
            this.getSetionsByFarmsiteId(this.pondCulture.farm_site_id);
          }
          break;
        default:
          break;
      }
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

   /**
    * @method getSetionsByFarmsiteId
    * @async
    * @description Fetches sections associated with a specific farm site ID.
    * Auto-selects the section if only one exists.
    * @param {string} farmSiteId - The ID of the farm site.
    * @side-effect Updates `pondSections` and `pondCulture.section_id`.
    */
   public async getSetionsByFarmsiteId(farmSiteId: string) {
    try {
      this.pondCulture.section_id = undefined;
      this.pondSections = await this.pondSectionService.getSectionsByFarmsiteId(farmSiteId);
      if (this.pondSections.length == 1) {
        this.pondCulture.section_id = this.pondSections[0].id; // model-binding key
        this.getPondsBySectionId(this.pondCulture.section_id);
      }
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

    /**
     * @method getPondsBySectionId
     * @async
     * @description Fetches ponds associated with a specific section ID.
     * Filters and formats the ponds into `pondsDetails_dropdown` for display, handling both preparatory and active ponds.
     * @param {string} sectionId - The ID of the section.
     * @side-effect Updates `customerPonds`, `pondsDetails_dropdown`, and `pondCulture.sectionName`.
     */
    public async getPondsBySectionId(sectionId: string) {
      this.pondCulture.pond_id = undefined;
      this.pondsDetails_dropdown = [];
      this.isSearchClicked = false;
      if (this.pondCulture.section_id) {
        try {
          this.customerPonds = await this.customerPondService.getPondsBySectionId(sectionId);
          this.pondsDetails_dropdown = [];
          if (this.customerPonds && this.customerPonds.length > 0) {
            for (let i = 0; i < this.customerPonds.length; i++) {
              let eachrec = this.customerPonds[i];
              if (eachrec.isPreparatory == true && (eachrec.PondCultureData && eachrec.PondCultureData.length > 0)) {
                for (let j = 0; j < eachrec.PondCultureData.length; j++) {
                  let eachpondculture = eachrec.PondCultureData[j];
                  eachpondculture.pond_name = eachrec.name;
                  eachpondculture.pond_id = eachrec.id;
                  this.pondsDetails_dropdown.push(eachpondculture);
                }
              } else if (eachrec.isPreparatory == false && eachrec.PondCultureData.length > 0) {
                let isPreObj = {
                  pond_id: eachrec.id,
                  pond_name: eachrec.name,
                };
                this.pondsDetails_dropdown.push(isPreObj);
              }
            }
          }
          /*find section name by selected businessid*/
          const selectedBusinessObj = _.filter(this.pondSections, (o) => {
            return o.id == this.pondCulture.section_id;
          });
          if (selectedBusinessObj.length > 0) {
            this.pondCulture.sectionName = selectedBusinessObj[0].section_name;
          }
          /*find business name by selected businessid*/
        } catch (error) {
          this.toastr.error(error?.error?.message || error?.message || error);
        }
      }
    }

  /**
   * @method getCulturesByPondId
   * @async
   * @description Fetches culture history for a specific pond ID.
   * @param {string} pondId - The ID of the pond.
   * @side-effect Updates `pondCultures` and `cultures`.
   */
  public async getCulturesByPondId(pondId: string) {
    this.pondCulture.pond_culture_id = '-1';
    try {
      this.pondCultures = await this.pondCultureService.getCulturesByPondId(Number(pondId));
      this.cultures = this.pondCultures;
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `registerForm`.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method onFiltersApplied
   * @description Applies filters to the traceability report list.
   * This is a central method that orchestrates filtering based on:
   * - Text search (`search` property).
   * - Culture selection (`isCultureSelected` property).
   * If no filters are active, it resets the list to the initial batch loaded state.
   * @side-effect Updates `pondTraceabilitiesData`, `isFiltering`, `batchLoader`.
   */
  public onFiltersApplied() {
    this.isFiltering = true;
    this.pondTraceabilitiesData = [];
    const value = this.search && this.search.replace(/[-_ :.]/g, '').toLowerCase();
    if (this.search != '') {
      if (this.isCultureSelected) {
        this.applySearchWithFilterType(value);
      } else {
        this.applySearchFilter(value);
      }
    } else {
      if (this.isCultureSelected) {
        this.applySearchWithFilterType(value);
      } else {
        this.isFiltering = false;
        this.initializeBatchLoader();
        this.batchLoader.reset();
        this.batchLoader.loadNextBatch(true);
      }
    }
  }

  /**
   * @method applySearchFilter
   * @private
   * @description Filters the report list based on a search string. Checks 'PondType' and 'CultureType'.
   * @param {string} searchValue - The normalized search string.
   */
  private applySearchFilter(searchValue: string) {
    this.pondTraceabilitiesData = this.dupPondTraceabilities.filter((traceabilitiesDetailsObj: any) =>
      ['PondType', 'CultureType'].some(property => {
        const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], traceabilitiesDetailsObj);
        return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
      })
    );
  }

  /**
   * @method applySearchWithFilterType
   * @private
   * @description Applies both culture ID filtering and text search filtering simultaneously.
   * @param {string} searchValue - The normalized search string.
   */
  private applySearchWithFilterType(searchValue: string) {

    this.pondTraceabilitiesData = this.dupPondTraceabilities.filter((traceabilitiesDetailsObj: any) => {
      let includeConditions = true; // Initialize shouldKeep as true

      if (this.isCultureSelected &&  traceabilitiesDetailsObj.pond_culture_id != Number(this.pondCulture.pond_culture_id)) {
        includeConditions = false; // Set shouldKeep to false if condition is not met
      }

      const includesSearch = ['PondType', 'CultureType'].some(property => {
        const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], traceabilitiesDetailsObj);
        return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
      })

      return this.search != '' ? includeConditions && includesSearch : includeConditions;
    });
  }

  /**
   * @method clearFilters
   * @description Clears specific filters based on the input type.
   * @param {string} filterFrom - The source of the clear action ('search' or default for culture).
   */
  public clearFilters(filterFrom: string) {
    switch (filterFrom) {
      case 'search':
        this.search = '';
        this.onFiltersApplied();
        break;
      default:
        this.isCultureSelected = false;
        this.pondCulture.pond_culture_id = -1;
        this.onFiltersApplied();
        break;
    }
  }

  /**
   * @method traceabilityReport
   * @async
   * @description Fetches the traceability report data from the server.
   * - Validates the form.
   * - Resets filters and search states.
   * - Calls the service to get data.
   * - Processes weather data strings.
   * - Initializes the batch loader for pagination.
   */
  public async traceabilityReport() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) {
      return;
    }
    this.submitted = false;
    this.dupPondTraceabilities = [];
    this.pondTraceabilitiesData = [];
    this.isFiltering = false;
    this.isSubmitClicked = false;
    this.search = '';
    this.isCultureSelected = false;
    this.pondCulture.pond_culture_id = -1;
    try {
      this.submitButtonDisabled = true;
      this.responseLoader = true;
      if (this.loginPersonType == 'Provider Manager') {
        let businessId = this.farmSites.find(siteid => siteid.id == this.pondCulture.farm_site_id);
        this.pondCulture.business_id = businessId.businessID;
      }
      let pondCulture = await this.pondCultureService.getTraceabilityReport(this.pondCulture.businessName, this.pondCulture.farmSiteName, this.pondCulture.sectionName, this.pondCulture.pond_id, -1);
      for (let i = 0; i < pondCulture.traceability.length; i++) {
        let eachObj = pondCulture.traceability[i];
        if (eachObj.weather != null) {
          pondCulture.traceability[i].weather = eachObj.weather.split(',').join('\n');
        }
      }
      this.dupPondTraceabilities = pondCulture.traceability;
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.submitted = false;    // Reset validation flag
      this.registerForm.markAsPristine(); // optional - clears dirty flags
      this.registerForm.markAsUntouched(); // optional - clears touched state
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    } finally {
      this.submitButtonDisabled = false;
      this.responseLoader = false;
      this.isSubmitClicked = true;
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers `checkScrollAndLoad` to handle infinite scrolling.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Debounced handler for scroll events. Checks if the user has scrolled near the bottom of the page.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility with the master data list and callback functions for UI updates.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.dupPondTraceabilities,
      (updatedList) => {
        this.pondTraceabilitiesData = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
    * @method
    * @description Toggles the fullscreen mode for the element with ID 'zoomView'.
    * @returns {void}
    */
  public openFullScreen(): void {
    let element = document.getElementById('zoomView') as HTMLElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  /**
   * @method exportExcel
   * @async
   * @description Generates and downloads an Excel report of the pond traceability data.
   * Constructs the request body with current context and user details.
   */
  public async exportExcel() {
    try {
      const reqBody = {
        "pond_culture_id": -1,
        "farm_site_id": this.pondCulture.farm_site_id,
        "section_id": Number(this.pondCulture.section_id),
        "pond_id": Number(this.pondCulture.pond_id),
        "business_id": this.pondCulture.business_id,
        "businessName": this.loginPersonType == 'EMPLOYEE' ? this.businessSearch : localStorage.getItem('loginBusinessName'),
        "farmNameAtLocation": this.farmSites.find((farmSiteObj: FarmSiteModel) => farmSiteObj.id == this.pondCulture.farm_site_id).farmNameAtLocation,
        "sectionName": this.pondSections.find((sectionsObj: PondSectionModel) => sectionsObj.id == this.pondCulture.section_id).section_name,
        "personName": localStorage.getItem('LoginUserName'),
        "mobileNumber": localStorage.getItem('loginPersonMobileNumber')
      }
      const fileObj = this.pondCultureService.excelReportsDownload('pondTraceability', reqBody);
      fileObj.subscribe(fileblob => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, 'Pond Traceability Report');
        }
      })
    } catch (error) {
      this.toastr.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests and cleans up the batch loader to prevent memory leaks.
   * @side-effect Cancels HTTP requests, destroys `batchLoader`.
   */
  public ngOnDestroy(): void {
    this.farmSitesService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.pondSectionService.cancelOnGoingHttpRequests();
    this.customerPondService.cancelOnGoingHttpRequests();
    this.pondCultureService.cancelOnGoingHttpRequests();
    this.personService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }
}
